// File: src/pages/ProfileQRPage.jsx
import React, { useContext, useState, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';
import { AuthContext } from '../context/AuthContext';

const ProfileQRPage = () => {
  const { user } = useContext(AuthContext); // Contains user data (name, email, etc.)

  // Prefer server-provided public_slug; fallback to email transformation
  const userSlug = useMemo(() => {
    if (user?.public_slug) return user.public_slug;
    if (user?.email) return user.email.replace('@', '-').replace(/\./g, '-');
    return 'unknown-user';
  }, [user]);
  const publicProfileURL = `${window.location.origin}/u/${userSlug}`;

  // State for QR code customization
  const [qrSize, setQrSize] = useState(200);
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [downloadFormat, setDownloadFormat] = useState('png');
  const [logoSrc, setLogoSrc] = useState(null);

  // QR container reference
  const qrRef = useRef(null);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setLogoSrc(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDownload = async () => {
    if (qrRef.current) {
      const canvas = await html2canvas(qrRef.current, { backgroundColor: null });
      const link = document.createElement('a');
      if (downloadFormat === 'png') {
        link.download = 'qr_code.png';
        link.href = canvas.toDataURL('image/png');
      } else if (downloadFormat === 'jpeg') {
        link.download = 'qr_code.jpeg';
        link.href = canvas.toDataURL('image/jpeg');
      }
      link.click();
    }
  };

  // ---------------- NFC TAB STATE & HANDLERS ----------------
  const [activeTab, setActiveTab] = useState('qr');
  const [nfcBusy, setNfcBusy] = useState(false);
  const [nfcLogs, setNfcLogs] = useState([]);
  const [nfcInput, setNfcInput] = useState(user?.email || '');
  const nfcSupported = typeof window !== 'undefined' && 'NDEFReader' in window;

  const log = (message) => {
    setNfcLogs((prev) => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const resetNfcDisplay = () => {
    setNfcLogs([]);
  };

  const buildTouchConnectUrlFromEmail = (email) => {
    const sanitized = (email || '').replace(/[@.]/g, '-').toLowerCase();
    return `${window.location.origin}/u/${sanitized}`;
  };

  const resolveUrlToWrite = () => {
    const input = (nfcInput || '').trim();
    if (!input) return null;
    if (input.startsWith('http://') || input.startsWith('https://')) return input;
    return buildTouchConnectUrlFromEmail(input);
  };

  const handleScanNfc = async () => {
    resetNfcDisplay();
    if (!nfcSupported) {
      log('Web NFC is not supported on this browser/device. Try Android Chrome.');
      return;
    }
    try {
      setNfcBusy(true);
      log('Starting NFC scan. Tap an NFC tag...');
      const ndef = new window.NDEFReader();
      await ndef.scan();
      ndef.addEventListener('readingerror', () => {
        log('Reading error: Could not read NFC tag.');
        setNfcBusy(false);
      });
      ndef.addEventListener('reading', ({ message, serialNumber }) => {
        log(`Serial Number: ${serialNumber}`);
        log(`Records: ${message.records.length}`);
        message.records.forEach((record, index) => {
          try {
            const decoder = new TextDecoder(record.encoding || 'utf-8');
            const data = decoder.decode(record.data);
            log(`Record #${index + 1} (${record.recordType}): ${data}`);
          } catch (e) {
            log(`Record #${index + 1} (${record.recordType}): [binary]`);
          }
        });
        setNfcBusy(false);
      });
    } catch (error) {
      log(`Error starting scan: ${error}`);
      setNfcBusy(false);
    }
  };

  const handleWriteNfc = async () => {
    resetNfcDisplay();
    if (!nfcSupported) {
      log('Web NFC is not supported on this browser/device. Try Android Chrome.');
      return;
    }
    const urlToWrite = resolveUrlToWrite();
    if (!urlToWrite) {
      log('Please enter a valid email or URL.');
      return;
    }
    try {
      setNfcBusy(true);
      log(`Writing URL to NFC tag: ${urlToWrite}`);
      const ndef = new window.NDEFReader();
      await ndef.write({
        records: [
          {
            recordType: 'url',
            data: urlToWrite,
          },
        ],
      });
      log('Successfully wrote to NFC tag.');
    } catch (error) {
      log(`Error writing tag: ${error}`);
    } finally {
      setNfcBusy(false);
    }
  };

  const handleMakeReadOnlyNfc = async () => {
    resetNfcDisplay();
    if (!nfcSupported) {
      log('Web NFC is not supported on this browser/device. Try Android Chrome.');
      return;
    }
    try {
      setNfcBusy(true);
      log('Attempting to make NFC tag read-only...');
      const ndef = new window.NDEFReader();
      await ndef.makeReadOnly();
      log('NFC tag has been made permanently read-only.');
    } catch (error) {
      log(`Error making tag read-only: ${error}`);
    } finally {
      setNfcBusy(false);
    }
  };

  return (
    <Container sx={{ textAlign: 'center', mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        QR Code & NFC Card
      </Typography>
      <Typography variant="body1" color="textSecondary">
        Generate your QR code or write your profile link to an NFC card.
      </Typography>

      {/* Tabs */}
      <Box sx={{ mt: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          centered
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab value="qr" label="QR Code" />
          <Tab value="nfc" label="NFC Card" />
        </Tabs>
      </Box>

      {activeTab === 'qr' && (
        <>
          {/* Customization Panel */}
          <Box
            sx={{
              mt: 4,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <TextField
              label="QR Size (px)"
              type="number"
              value={qrSize}
              onChange={(e) => setQrSize(Number(e.target.value))}
              sx={{ width: 150 }}
            />
            <TextField
              label="Foreground Color"
              type="color"
              value={foregroundColor}
              onChange={(e) => setForegroundColor(e.target.value)}
              sx={{ width: 100 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Background Color"
              type="color"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              sx={{ width: 100 }}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="download-format-label">Format</InputLabel>
              <Select
                labelId="download-format-label"
                value={downloadFormat}
                label="Format"
                onChange={(e) => setDownloadFormat(e.target.value)}
              >
                <MenuItem value="png">PNG</MenuItem>
                <MenuItem value="jpeg">JPEG</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" component="label">
              Upload Logo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleLogoUpload}
              />
            </Button>
          </Box>

          {/* QR Code Display */}
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              mt: 4,
              p: 2,
              border: '1px solid #ccc',
            }}
            ref={qrRef}
          >
            <QRCode
              value={publicProfileURL}
              size={qrSize}
              fgColor={foregroundColor}
              bgColor={backgroundColor}
              level="H"
            />
            {logoSrc && (
              <Box
                component="img"
                src={logoSrc}
                alt="Logo"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: qrSize * 0.25,
                  height: qrSize * 0.25,
                  borderRadius: '50%',
                  border: '2px solid #fff',
                }}
              />
            )}
          </Box>

          {/* Download Button */}
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleDownload}>
              Download QR Code
            </Button>
          </Box>
        </>
      )}

      {activeTab === 'nfc' && (
        <Box sx={{ mt: 4, maxWidth: 720, mx: 'auto', textAlign: 'left' }}>
          <Typography variant="h6" gutterBottom>
            Write Your Profile Link to an NFC Card
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Enter your TouchConnect login email or paste a full URL starting with https://. On supported
            Android devices (Chrome), you can read, write, and lock NFC tags.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField
              label="Email or URL (https://...)"
              fullWidth
              value={nfcInput}
              onChange={(e) => setNfcInput(e.target.value)}
            />
            <Button
              variant="outlined"
              onClick={() => setNfcInput(user?.email || '')}
              disabled={!user?.email}
            >
              Use my email
            </Button>
            <Button
              variant="outlined"
              onClick={() => setNfcInput(publicProfileURL)}
            >
              Use my profile URL
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button variant="contained" onClick={handleScanNfc} disabled={nfcBusy}>
              Read NFC Tag
            </Button>
            <Button variant="contained" onClick={handleWriteNfc} disabled={nfcBusy}>
              Write to NFC Tag
            </Button>
            <Button variant="contained" color="error" onClick={handleMakeReadOnlyNfc} disabled={nfcBusy}>
              Lock NFC Tag (Read-Only)
            </Button>
            <Button variant="outlined" onClick={resetNfcDisplay} disabled={nfcBusy}>
              Reset Display
            </Button>
          </Box>

          {!nfcSupported && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              Web NFC is not supported in this browser. Try Chrome on Android.
            </Typography>
          )}

          <Paper variant="outlined" sx={{ p: 2, minHeight: 140, maxHeight: 260, overflowY: 'auto' }}>
            {nfcLogs.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                NFC logs will appear here.
              </Typography>
            ) : (
              nfcLogs.map((line, idx) => (
                <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                  {line}
                </Typography>
              ))
            )}
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default ProfileQRPage;
